import { Badge, Button, FormFeedback, Input, Table } from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getSupplierOptions } from "features/Suppliers/services/SupplierService";

interface Props {
  validation: any;
  readOnly?: boolean;
}

const LOCKED_STATUSES = new Set(["IN_PROGRESS", "COMPLETED", "CANCELLED"]);

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  ASSIGNED: { label: "Assigned", color: "info" },
  IN_PROGRESS: { label: "In Progress", color: "warning" },
  COMPLETED: { label: "Completed", color: "success" },
  CANCELLED: { label: "Cancelled", color: "secondary" },
};

const emptySubcontractRow = () => ({
  supplierId: "",
  _supplierLabel: "",
  description: "",
  agreedAmount: "",
  finalAmount: "",
  labourCount: "",
  notes: "",
});

export default function WorkOrderSubcontractsFieldArray({
  validation,
  readOnly = false,
}: Props) {
  const rows: any[] = validation.values.subcontracts ?? [];

  const addRow = () => {
    validation.setFieldValue("subcontracts", [...rows, emptySubcontractRow()]);
  };

  const removeRow = (idx: number) => {
    validation.setFieldValue(
      "subcontracts",
      rows.filter((_: any, i: number) => i !== idx),
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="text-uppercase text-muted fw-semibold fs-12">
            Subcontracts
          </div>
          <p className="text-muted small mb-0 mt-1">
            Outsourced portions of this work order
          </p>
        </div>
        {!readOnly && (
          <Button
            type="button"
            color="primary"
            size="sm"
            outline
            onClick={addRow}
          >
            <i className="ri-add-line me-1" />
            Add subcontractor
          </Button>
        )}
      </div>
      <div className="border rounded p-3 bg-light bg-opacity-25">
        {rows.length === 0 ? (
          <p className="text-muted text-center mb-0 py-2">
            No subcontracts added.
            {!readOnly && ' Click "Add subcontractor" to add one.'}
          </p>
        ) : (
          <div className="table-responsive">
            <Table className="table-borderless align-center mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 200 }}>Subcontractor</th>
                  <th style={{ minWidth: 180 }}>Description</th>
                  <th style={{ minWidth: 120 }}>Agreed amount</th>
                  <th style={{ minWidth: 120 }}>Final amount</th>
                  <th style={{ minWidth: 90 }}>Labour</th>
                  <th style={{ minWidth: 150 }}>Notes</th>
                  {!readOnly && <th style={{ width: 48 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const prefix = `subcontracts[${idx}]`;
                  const rowErrors: any =
                    (validation.errors.subcontracts ?? [])[idx] ?? {};
                  const rowTouched: any =
                    (validation.touched.subcontracts ?? [])[idx] ?? {};
                  const isLocked = readOnly || LOCKED_STATUSES.has(row.status);
                  const statusBadge = row.status
                    ? STATUS_BADGE[row.status]
                    : null;

                  return (
                    <tr key={idx}>
                      <td>
                        {isLocked ? (
                          <span className="fw-medium">
                            {row._supplierLabel || "—"}
                          </span>
                        ) : (
                          <>
                            <AsyncPaginatedSelect
                              key={`sc-supplier-${idx}`}
                              fetchOptions={getSupplierOptions}
                              placeholder="Search..."
                              defaultOption={
                                row.supplierId && row._supplierLabel
                                  ? {
                                      value: row.supplierId,
                                      label: row._supplierLabel,
                                    }
                                  : undefined
                              }
                              value={row.supplierId || ""}
                              onChange={(val: any) => {
                                validation.setFieldValue(
                                  `${prefix}.supplierId`,
                                  val?.value ?? "",
                                );
                                validation.setFieldValue(
                                  `${prefix}._supplierLabel`,
                                  val?.label ?? "",
                                );
                              }}
                              onBlur={() =>
                                validation.setFieldTouched(
                                  `${prefix}.supplierId`,
                                  true,
                                )
                              }
                              isInvalid={
                                !!(
                                  rowTouched.supplierId && rowErrors.supplierId
                                )
                              }
                              menuPosition="absolute"
                              menuPlacement="top"
                            />
                            {rowTouched.supplierId && rowErrors.supplierId ? (
                              <div className="text-danger small mt-1">
                                {rowErrors.supplierId}
                              </div>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td>
                        <Input
                          type="text"
                          name={`${prefix}.description`}
                          placeholder="Scope of work"
                          value={row.description ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(rowTouched.description && rowErrors.description)
                          }
                          disabled={isLocked}
                        />
                        {rowTouched.description && rowErrors.description ? (
                          <FormFeedback>{rowErrors.description}</FormFeedback>
                        ) : null}
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          name={`${prefix}.agreedAmount`}
                          placeholder="0.00"
                          value={row.agreedAmount ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              rowTouched.agreedAmount && rowErrors.agreedAmount
                            )
                          }
                          disabled={isLocked}
                        />
                        {rowTouched.agreedAmount && rowErrors.agreedAmount ? (
                          <FormFeedback>{rowErrors.agreedAmount}</FormFeedback>
                        ) : null}
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          name={`${prefix}.finalAmount`}
                          placeholder="0.00"
                          value={row.finalAmount ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={readOnly}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          name={`${prefix}.labourCount`}
                          placeholder="—"
                          value={row.labourCount ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          name={`${prefix}.notes`}
                          placeholder="Optional"
                          value={row.notes ?? ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                        />
                      </td>
                      {!readOnly && (
                        <td>
                          {isLocked && statusBadge ? (
                            <Badge color={statusBadge.color} className="ms-1">
                              {statusBadge.label}
                            </Badge>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => removeRow(idx)}
                            >
                              <i className="ri-delete-bin-line fs-16" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
